import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { debounce, noop } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage as setRunsCurrentPage,
  setPageSize,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
} from 'dashboard/redux/actions/workflow-runs';
import {
  clearWorkflowVersions,
  setCurrentPage as setVersionsCurrentPage,
} from 'dashboard/redux/actions/workflow-versions';
import { RootState } from 'dashboard/redux/store';
import { loadWorkflowRunList } from 'dashboard/redux/thunk-actions/workflow-runs';
import DashboardWorkflowRunsView from 'dashboard/views/DashboardWorkflowRunsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';
import { getDateRange, getLuxonDateRange, translateDateValuetoLuxon } from 'lib/utils/filters';

type ConnectedInquiryTemplateWorkflowRunsProps = PropsFromRedux & {
  workflowVersions: WorkflowVersion[];
};

const ConnectedInquiryTemplateWorkflowRuns = (props: ConnectedInquiryTemplateWorkflowRunsProps) => {
  const {
    currentEnvironment,
    pageSize,
    runsCurrentPage,
    versionsCurrentPage,
    versionsTotalPages,
    workflowRuns,
    workflowRunFilters,
    workflowRunSortParams,
    workflowVersions,
  } = props;

  useEffect(() => {
    props.setWorkflowRunFilters({
      workflowVersionIds: workflowVersions.map((version) => version.id),
      triggerType: ApiModel.TriggerDirect,
    });
  }, []);

  useOnMountAndUnmount(props.clearWorkflowVersions);

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    currentPage: runsCurrentPage,
    filters: workflowRunFilters,
    pageSize: pageSize,
    sortParams: workflowRunSortParams,
    setCurrentPage: props.setRunsCurrentPage,
    setFilters: props.setWorkflowRunFilters,
    setPageSize: props.setPageSize,
    setSortParams: props.setWorkflowRunSortParams,
  });

  const { loading: isLoadingWorkflowRuns } = useApiLoader({
    loader: () =>
      props.loadWorkflowRunList({
        currentEnvironment,
        currentPage: runsCurrentPage,
        pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: workflowRunFilters,
        sortParams: workflowRunSortParams,
      }),
    deps: [
      currentEnvironment,
      pageSize,
      runsCurrentPage,
      workflowRunFilters,
      workflowRunSortParams,
      props.loadWorkflowRunList,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflow runs.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleDateUpdate = useCallback(
    (range: {
      startDate?: DateTime | Iso8601DateString;
      endDate?: DateTime | Iso8601DateString;
    }) => {
      const { startDate, endDate } = range;

      if (startDate || endDate) {
        const { start, end } =
          startDate instanceof DateTime || endDate instanceof DateTime
            ? getLuxonDateRange(startDate as DateTime, endDate as DateTime)
            : getDateRange(startDate as Iso8601DateString, endDate as Iso8601DateString);
        props.setWorkflowRunFilters({ completedAtStart: start, completedAtEnd: end });
      } else {
        props.setWorkflowRunFilters({ completedAtStart: undefined, completedAtEnd: undefined });
      }
      props.setRunsCurrentPage(1);
    },
    [props.setWorkflowRunFilters, props.setRunsCurrentPage],
  );

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) handleDateUpdate({ startDate, endDate });
      }
    },
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = useCallback(
    (status: WorkflowRunStatus[]) => {
      props.setWorkflowRunFilters({ status });
      props.setRunsCurrentPage(1);
    },
    [props.setWorkflowRunFilters, props.setRunsCurrentPage],
  );

  const handleVersionFilterUpdate = useCallback(
    (workflowVersionIds: string[]) => {
      props.setWorkflowRunFilters({ workflowVersionIds });
      props.setRunsCurrentPage(1);
    },
    [props.setRunsCurrentPage, props.setWorkflowRunFilters],
  );

  const handleOmniFilterUpdate = useCallback(
    debounce(500, (omni: string) => {
      props.setWorkflowRunFilters({ omni });
      props.setRunsCurrentPage(1);
    }),
    [props.setRunsCurrentPage, props.setWorkflowRunFilters],
  );

  const onLoadMoreVersions =
    versionsCurrentPage < versionsTotalPages
      ? () => props.setVersionsCurrentPage(versionsCurrentPage + 1)
      : null;

  const workflowId = workflowVersions[0]?.relationships.workflow.data.id;

  return (
    <DashboardWorkflowRunsView
      workflowRuns={workflowRuns}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowRunFilters={workflowRunFilters}
      workflowId={workflowId}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowRunPaginationParams={props.workflowRunPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      workflowVersions={workflowVersions}
      workflowsMap={{}}
      isLoadingWorkflowVersions={false}
      isLoadingWorkflowRuns={isLoadingWorkflowRuns}
      isLoadingBulkResumeWorkflowRuns={false}
      isLoadingCreateBulkManualWorkflowRuns={false}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={workflowRunSortParams}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleVersionFilterUpdate={handleVersionFilterUpdate}
      handleSortUpdate={handleSortUpdate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onLoadMoreVersions={onLoadMoreVersions}
      handleOmniFilterUpdate={handleOmniFilterUpdate}
      handleBulkCreateManualWorkflowRuns={noop}
      handleResumeFormSubmit={noop}
      clearManualRunResults={noop}
      showMessage={props.showMessage}
      currentEnvironment={currentEnvironment}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    pageSize: state.workflowRuns.pageSize,
    runsCurrentPage: state.workflowRuns.currentPage,
    versionsCurrentPage: state.workflowVersions.currentPage,
    versionsTotalPages: state.workflowVersions.paginationParams?.['total-pages'],
    workflowRunFilters: state.workflowRuns.filters,
    workflowRunPaginationParams: state.workflowRuns.paginationParams,
    workflowRuns:
      state.workflowRuns.workflowRuns[state.currentEnvironment.currentEnvironment.id] || [],
    workflowRunSortParams: state.workflowRuns.sortParams,
  };
};

const mapDispatchToProps = {
  clearWorkflowVersions,
  loadWorkflowRunList,
  setPageSize,
  setRunsCurrentPage,
  setVersionsCurrentPage,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedInquiryTemplateWorkflowRuns);
